import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { GlobalAdmissionLimitValue, SuperadminOnlineAdmissionApi } from '@/services/superadmin-online-admission.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';

interface MessageRule {
  key: string;
  message: string;
  classNames: string[];
}

/**
 * Superadmin — Global Admission Limits. Genuinely platform-wide, not
 * per-school: one row per class NAME, matched across every school that
 * has a class with that name (see backend OnlineAdmissionAdminService's
 * doc comment). A class with every field left blank has no restriction.
 * `readOnly` — org-tier callers pass this (backend only allows writes via
 * `SuperadminGuard`), same pattern as `MaterialItemsPage`/`ProgramItemsPage`.
 *
 * "Message Rules" (top section) is real legacy's own UX for editing the
 * SAME per-class `message` column the DOB table below also reads/writes —
 * not a separate table or endpoint (confirmed against the real source:
 * `admission_limits.blade.php`'s rule UI is pure client-side grouping —
 * classes sharing an identical message string become one visual "rule",
 * and saving fans the rule's message back out to every selected class,
 * newline-joining if a class ends up in more than one rule). Mirrored
 * here: `rules` is local UI state only, reconciled into the same
 * `edits[className].message` shape at save time — no new DTO/entity.
 */
export function GlobalAdmissionLimitsPage({ readOnly = false }: { readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery({
    queryKey: ['superadmin', 'online-admission-limits'],
    queryFn: () => SuperadminOnlineAdmissionApi.listLimits(),
  });

  const [edits, setEdits] = useState<Record<string, GlobalAdmissionLimitValue>>({});
  const [rules, setRules] = useState<MessageRule[]>([]);
  const nextRuleId = useRef(0);

  useEffect(() => {
    if (!data) return;
    const seeded: Record<string, GlobalAdmissionLimitValue> = {};
    for (const name of data.classNames) {
      const existing = data.limits.find((l) => l.className === name);
      seeded[name] = { minDob: existing?.minDob ?? undefined, maxDob: existing?.maxDob ?? undefined, message: existing?.message ?? undefined };
    }
    setEdits(seeded);

    // Group classes that share an identical message into one rule, same as
    // real legacy's own load-time grouping.
    const byMessage = new Map<string, string[]>();
    for (const name of data.classNames) {
      const msg = seeded[name]?.message?.trim();
      if (!msg) continue;
      const list = byMessage.get(msg) ?? [];
      list.push(name);
      byMessage.set(msg, list);
    }
    setRules(
      [...byMessage.entries()].map(([message, classNames]) => ({
        key: `rule-${nextRuleId.current++}`,
        message,
        classNames,
      })),
    );
  }, [data]);

  /** Fan each rule's message back out to its selected classes (newline-joined if a class is in more than one rule) — same as real legacy's save-time reconciliation. */
  function buildEditsFromRules(): Record<string, GlobalAdmissionLimitValue> {
    const next: Record<string, GlobalAdmissionLimitValue> = {};
    for (const name of data?.classNames ?? []) {
      next[name] = { ...edits[name], message: undefined };
    }
    for (const rule of rules) {
      const msg = rule.message.trim();
      if (!msg) continue;
      for (const className of rule.classNames) {
        const existing = next[className]?.message;
        next[className] = { ...next[className], message: existing ? `${existing}\n${msg}` : msg };
      }
    }
    return next;
  }

  const save = useMutation({
    mutationFn: () => SuperadminOnlineAdmissionApi.updateLimits(buildEditsFromRules()),
    onSuccess: (res) => qc.setQueryData(['superadmin', 'online-admission-limits'], res),
  });

  const setField = (className: string, field: 'minDob' | 'maxDob', value: string) => {
    setEdits((prev) => ({ ...prev, [className]: { ...prev[className], [field]: value || undefined } }));
  };

  function addRule() {
    setRules((rs) => [...rs, { key: `rule-${nextRuleId.current++}`, message: '', classNames: [] }]);
  }
  function updateRuleMessage(key: string, message: string) {
    setRules((rs) => rs.map((r) => (r.key === key ? { ...r, message } : r)));
  }
  function updateRuleClasses(key: string, classNames: string[]) {
    setRules((rs) => rs.map((r) => (r.key === key ? { ...r, classNames } : r)));
  }
  function deleteRule(key: string) {
    setRules((rs) => rs.filter((r) => r.key !== key));
  }

  return (
    <>
      <PageHeader
        title="Global Admission Limits"
        description="Date-of-birth eligibility and custom messages per class name — applied across every school with a matching class."
        actions={
          readOnly ? undefined : (
            <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending || isFetching}>
              {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
              Save Changes
            </button>
          )
        }
      />

      {save.isSuccess && <div className="mb-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">Saved.</div>}
      {save.isError && <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">Could not save changes.</div>}

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Message Rules</h2>
        <p className="mb-4 text-sm text-slate-500">Add custom messages and select which classes they apply to.</p>

        <div className="space-y-3">
          {rules.map((rule) => (
            <div key={rule.key} className="card flex items-start gap-4 p-4">
              <div className="flex-1">
                <label className="mb-1 block text-sm font-medium text-slate-700">Message</label>
                <Input
                  value={rule.message}
                  onChange={(e) => updateRuleMessage(rule.key, e.target.value)}
                  placeholder="Enter message here…"
                  disabled={readOnly}
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-sm font-medium text-slate-700">Apply to Classes</label>
                <Select
                  multiple
                  className="!h-28"
                  value={rule.classNames}
                  onChange={(e) => updateRuleClasses(rule.key, Array.from(e.target.selectedOptions, (o) => o.value))}
                  disabled={readOnly}
                >
                  {(data?.classNames ?? []).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-slate-400">Hold Ctrl/Cmd to select multiple</p>
              </div>
              {!readOnly && (
                <button
                  className="mt-6 shrink-0 rounded-md bg-red-50 p-2 text-red-600 hover:bg-red-100"
                  onClick={() => deleteRule(rule.key)}
                  title="Delete rule"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {rules.length === 0 && (
            <div className="card p-6 text-center text-sm text-slate-400">No message rules yet.</div>
          )}
        </div>

        {!readOnly && (
          <button className="btn-primary mt-4 inline-flex items-center gap-1.5" onClick={addRule}>
            <Plus className="h-4 w-4" /> Add New Message Rule
          </button>
        )}
      </div>

      <h2 className="mb-2 text-lg font-semibold text-slate-900">Date of Birth Ranges</h2>
      <div className="card overflow-x-auto p-0">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Class Name</th>
              <th className="px-4 py-3">Min DOB (oldest allowed)</th>
              <th className="px-4 py-3">Max DOB (youngest allowed)</th>
            </tr>
          </thead>
          <tbody>
            {isFetching && !data ? (
              <tr>
                <td colSpan={3} className="py-8 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : (
              (data?.classNames ?? []).map((name) => (
                <tr key={name} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2 font-medium text-slate-800">{name}</td>
                  <td className="px-4 py-2">
                    <Input type="date" value={edits[name]?.minDob ?? ''} onChange={(e) => setField(name, 'minDob', e.target.value)} disabled={readOnly} />
                  </td>
                  <td className="px-4 py-2">
                    <Input type="date" value={edits[name]?.maxDob ?? ''} onChange={(e) => setField(name, 'maxDob', e.target.value)} disabled={readOnly} />
                  </td>
                </tr>
              ))
            )}
            {data && data.classNames.length === 0 && (
              <tr>
                <td colSpan={3} className="py-8 text-center text-slate-400">
                  No classes found across any school.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
